import React, { FC, useEffect, useState } from 'react';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import classNames from 'classnames';
import { NavLink, useParams } from 'react-router-dom';
import { calculateNewUrl } from '../../core/i18n/locale';
import styles from './LeftMenu.module.styl';
import { IMenu } from 'layouts/front-layout/interfaces';
import { IProps, IMenuItem, RouteParams } from './interfaces';
import { addChildrenToTree } from './helper';

const LeftMenu: FC<IProps> = ({ fetchNestedSideMenu, leftMenu, level = 1 }) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [leftMenuItems, setLeftMenuItems] = useState(leftMenu);
  const { id1, locale } = useParams<RouteParams>();

  useEffect(() => {
    if (id1) {
      setExpanded({});
    }
  }, [id1]);
  const getTitle = (item: IMenuItem): string => {
    const { translation, title } = item;
    return locale && translation && translation[locale]
      ? JSON.parse(translation[locale] as any).title
      : title;
  };

  const toggleCollapsed = (id: string) => {
    setExpanded({
      ...expanded,
      [id]: !expanded[id],
    });
  };

  const toggleCollapsedButton = async (leftMenuItem: IMenu) => {
    // if we fetch children already, we don't need to fetch them again
    if (leftMenuItem?.children?.length > 0) {
      toggleCollapsed(leftMenuItem.id);
      return;
    }
    const { items: newChildren } = await fetchNestedSideMenu(leftMenuItem.id);
    const updatedLeftMenu = addChildrenToTree(
      leftMenuItems,
      newChildren,
      leftMenuItem.param,
    );
    toggleCollapsed(leftMenuItem.id);
    setLeftMenuItems(updatedLeftMenu);
  };
  const renderMenuTitle = (leftMenuItem: IMenu) => {
    if (!leftMenuItem.hasChildren) {
      return (
        <NavLink
          className={styles.expandedTitle}
          to={calculateNewUrl(locale, `/${leftMenuItem.link}`)}
        >
          <span className={styles.menuLabel}>{getTitle(leftMenuItem)}</span>
        </NavLink>
      );
    }

    return leftMenuItem.type === 'parent' ? (
      <button
        type={'button'}
        className={
          expanded[leftMenuItem.id]
            ? styles.expandedTitle
            : styles.collapsedTitle
        }
        onClick={() => toggleCollapsedButton(leftMenuItem)}
      >
        <span className={styles.menuLabel}>
          {getTitle(leftMenuItem)}
          <KeyboardArrowDown className={styles.arrow} />
        </span>
      </button>
    ) : (
      <NavLink
        className={
          expanded[leftMenuItem.id]
            ? styles.expandedTitle
            : styles.collapsedTitle
        }
        to={calculateNewUrl(locale, `/${leftMenuItem.link}`)}
        onClick={() => toggleCollapsedButton(leftMenuItem)}
      >
        <span className={styles.menuLabel}>
          {getTitle(leftMenuItem)}
          <KeyboardArrowDown className={styles.arrow} />
        </span>
      </NavLink>
    );
  };

  if (!Array.isArray(leftMenu) || leftMenu.length === 0) {
    return null;
  }

  return (
    <div className={styles.root}>
      {leftMenuItems.map(leftMenuItem => {
        return (
          <div
            className={classNames({ [styles.rootWithBorder]: level === 1 })}
            key={leftMenuItem.id}
          >
            {renderMenuTitle(leftMenuItem)}
            {leftMenuItem?.children?.length > 0 && (
              <div
                className={
                  expanded[leftMenuItem.id]
                    ? styles.expandedSubmenu
                    : styles.collapsedSubmenu
                }
              >
                <LeftMenu
                  leftMenu={leftMenuItem.children || []}
                  level={level + 1}
                  fetchNestedSideMenu={fetchNestedSideMenu}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default LeftMenu;
